// src/utils/occSymbol.js (ESM twin of the backend module — keep in lockstep) — OCC option symbol build/parse/format.
//
// OCC (OSI) format: ROOT + YYMMDD + C|P + strike×1000 zero-padded to 8 digits.
//   AAPL 2026-09-18 call $230  →  'AAPL260918C00230000'
//
// The root is the underlying ticker (1–6 alphanumerics here; dotted roots like
// BRK.B are out of scope for v1 — the trading layer rejects them upstream).
// This module is duplicated byte-for-byte in crypto-backend-trade/utils and
// crypto-frontend/src/utils (ESM shim) — keep the three in lockstep.


const OCC_RE = /^([A-Z][A-Z0-9]{0,5})(\d{6})([CP])(\d{8})$/;

/** True when the string is a well-formed OCC option symbol. */
function isOccSymbol(s) {
  return OCC_RE.test(String(s || '').toUpperCase().trim());
}

/**
 * Build an OCC symbol.
 * @param {{underlying: string, expiry: string, right: 'call'|'put', strike: number}} p
 *   expiry: 'YYYY-MM-DD'. strike in dollars (e.g. 230, 12.5, 0.75).
 */
function buildOccSymbol({ underlying, expiry, right, strike }) {
  const root = String(underlying || '').toUpperCase().trim();
  if (!/^[A-Z][A-Z0-9]{0,5}$/.test(root)) {
    throw new Error(`occSymbol: invalid underlying root "${underlying}"`);
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(expiry || ''));
  if (!m) throw new Error(`occSymbol: expiry must be YYYY-MM-DD, got "${expiry}"`);
  const yymmdd = m[1].slice(2) + m[2] + m[3];
  const r = String(right || '').toLowerCase();
  if (r !== 'call' && r !== 'put') throw new Error(`occSymbol: right must be call|put, got "${right}"`);
  const k = Number(strike);
  if (!Number.isFinite(k) || k <= 0) throw new Error(`occSymbol: invalid strike ${strike}`);
  // ×1000 with rounding — strikes are decimal dollars with up to 3dp (12.5 → 12500).
  const kInt = Math.round(k * 1000);
  if (kInt >= 1e8) throw new Error(`occSymbol: strike ${strike} too large for OCC format`);
  return `${root}${yymmdd}${r === 'call' ? 'C' : 'P'}${String(kInt).padStart(8, '0')}`;
}

/**
 * Parse an OCC symbol. Returns null (never throws) on anything malformed —
 * callers use this as a detector on arbitrary symbol strings.
 * @returns {{underlying: string, expiry: string, right: 'call'|'put', strike: number} | null}
 */
function parseOccSymbol(s) {
  const m = OCC_RE.exec(String(s || '').toUpperCase().trim());
  if (!m) return null;
  const [, root, yymmdd, cp, kRaw] = m;
  const yy = Number(yymmdd.slice(0, 2));
  const mm = yymmdd.slice(2, 4);
  const dd = yymmdd.slice(4, 6);
  // OCC-listed contracts are all 21st-century; 2000+yy is safe for decades.
  const expiry = `${2000 + yy}-${mm}-${dd}`;
  const mmN = Number(mm), ddN = Number(dd);
  if (mmN < 1 || mmN > 12 || ddN < 1 || ddN > 31) return null;
  return {
    underlying: root,
    expiry,
    right: cp === 'C' ? 'call' : 'put',
    strike: Number(kRaw) / 1000,
  };
}

/** Human-readable form: 'AAPL 09/18/26 $230 C'. Falls back to the input. */
function formatOccForDisplay(s) {
  const p = parseOccSymbol(s);
  if (!p) return String(s || '');
  const [y, m, d] = p.expiry.split('-');
  const strikeStr = Number.isInteger(p.strike) ? String(p.strike) : String(p.strike);
  return `${p.underlying} ${m}/${d}/${y.slice(2)} $${strikeStr} ${p.right === 'call' ? 'C' : 'P'}`;
}

export { isOccSymbol, buildOccSymbol, parseOccSymbol, formatOccForDisplay, OCC_RE };
